/**
 * The sandbox allowlist (decision 0096, design `docs/design/sandbox-permissions.md` steps 1–2): what a run's CLI may do
 * without asking, beyond its own sandbox. People edit it (workspace and project settings, or "Always allow" on a
 * permission card); agents never write it. The cloud puts the effective list (workspace ∪ project) into the signed job
 * (`JobSpec.access.allow`) for runners with the manifest feature `access_allow`; the runner maps each rule to the CLI's
 * NATIVE config (Codex writable roots, Claude's flag settings layer) and writes nothing into the person's CLI homes.
 */
import { Schema } from "effect"
import { Millis, RepoId, RunnerId, UserId } from "./ids"

// #region allow
/** The OS an entry is for (a macOS-only path is skipped on Linux). Absent = every OS. */
export const AllowOs = Schema.Literals(["darwin", "linux"])
export type AllowOs = typeof AllowOs.Type

/** An absolute path or one under the home dir (`~/…`, expanded by the runner against its own HOME). */
export const AllowPath = Schema.String.check(
  Schema.isMaxLength(500),
  Schema.isPattern(/^(?:~\/|\/)[^\0\n\r]*$/),
)
/** One word of a command prefix: no whitespace, no control characters. */
export const AllowWord = Schema.String.check(Schema.isMaxLength(100), Schema.isPattern(/^[^\s\0]+$/))
/** A host name, optionally with a leading `*.` wildcard (`registry.npmjs.org`, `*.github.com`). */
export const AllowHost = Schema.String.check(
  Schema.isMaxLength(253),
  Schema.isPattern(/^(?:\*\.)?[A-Za-z0-9](?:[A-Za-z0-9-]{0,62})(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,62}))*$/),
)

export const AllowWritableRoot = Schema.Struct({
  kind: Schema.Literal("writableRoot"),
  path: AllowPath,
  os: Schema.optionalKey(AllowOs),
})
export const AllowCommand = Schema.Struct({
  kind: Schema.Literal("command"),
  /** `["wrangler"]`, `["pnpm", "install"]`: commands that start with these words. */
  prefix: Schema.Array(AllowWord).check(Schema.isMinLength(1), Schema.isMaxLength(8)),
})
export const AllowNetworkHost = Schema.Struct({ kind: Schema.Literal("networkHost"), host: AllowHost })

/** An MCP server's name as the CLIs see it (`posthog`, `codex_apps`, `linear-server`). */
export const AllowServer = Schema.String.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(100),
  Schema.isPattern(/^[A-Za-z0-9_.-]+$/),
)
/** One tool of a server (`query-run`, `posthog.exec`), or `*` for every tool it has. */
export const AllowToolName = Schema.String.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(200),
  Schema.isPattern(/^(?:\*|[A-Za-z0-9_.:/-]+)$/),
)
/**
 * Decision 0138: an MCP tool or connector that runs without a card. A named `tool` covers that tool. `tool: "*"` covers
 * the server's tools that only read (by name: `get…`, `list…`, `search…`, …, `mcpToolReads`), and every tool with
 * `writes: true`. A grant says what it covers: a call beyond it (a write tool under a read-only grant) still asks.
 * Only for a runner with the feature `owner_grants`; the cloud leaves these rules out of older runners' jobs.
 */
export const AllowMcpTool = Schema.Struct({
  kind: Schema.Literal("mcpTool"),
  server: AllowServer,
  tool: AllowToolName,
  writes: Schema.optionalKey(Schema.Boolean),
})

/** One rule: a folder the CLI may write, a command prefix, a network host, or an MCP tool, that runs without asking. */
export const AllowRule = Schema.Union([AllowWritableRoot, AllowCommand, AllowNetworkHost, AllowMcpTool])
export type AllowRule = typeof AllowRule.Type

/**
 * A stored rule with who added it, when and from where (Settings or "Always allow" on a card). Decision 0138: `repo`
 * scopes it to runs in that repo (absent = every run the list applies to); the cloud resolves the scope when it builds
 * a job, so a runner only ever sees rules.
 */
export const AllowEntry = Schema.Struct({
  rule: AllowRule,
  addedBy: UserId,
  addedAt: Millis,
  source: Schema.Literals(["settings", "card"]),
  note: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(200))),
  repo: Schema.optionalKey(RepoId),
})
export type AllowEntry = typeof AllowEntry.Type

/** At most this many entries per list (workspace, project, a runner owner's). */
export const ALLOW_MAX = 50

/** The identity of a rule: two rules with the same key are the same entry. */
export const allowKey = (r: AllowRule): string => {
  switch (r.kind) {
    case "writableRoot":
      return `writableRoot:${r.os ?? "*"}:${r.path.replace(/\/+$/, "") || "/"}`
    case "command":
      return `command:${JSON.stringify(r.prefix)}`
    case "networkHost":
      return `networkHost:${r.host.toLowerCase()}`
    case "mcpTool":
      return `mcpTool:${r.server}:${r.tool}:${r.tool === "*" && r.writes === true ? "w" : "r"}`
  }
}

/** The identity of an entry: its rule and its repo scope (the same rule for two repos is two entries). */
export const allowEntryKey = (e: { readonly rule: AllowRule; readonly repo?: string | undefined }): string =>
  `${e.repo ?? "*"}|${allowKey(e.rule)}`

/** The rules of a list that apply to a run in `repoId` (null = a run without a repo: unscoped entries only). */
export const allowRulesFor = (
  entries: ReadonlyArray<{ readonly rule: AllowRule; readonly repo?: string | undefined }>,
  repoId: string | null,
): AllowRule[] => entries.filter((e) => e.repo === undefined || e.repo === repoId).map((e) => e.rule)

/** Rules a runner without the feature `owner_grants` can decode (it knows no `mcpTool` kind). */
export const withoutMcpToolRules = (rules: ReadonlyArray<AllowRule>): AllowRule[] =>
  rules.filter((r) => r.kind !== "mcpTool")

/** Tool names that read: the name starts with a read verb (`list_projects`, `posthog.query-run`, `getUser`). */
const READ_VERB =
  /^(?:get|list|search|read|query|fetch|find|describe|show|view|retrieve|count|lookup|whoami|status|info|check|preview|explain)/i

/** Decision 0138: whether an MCP tool only reads, judged by its name (an unknown tool counts as one that writes). */
export const mcpToolReads = (tool: string | null): boolean => {
  if (tool === null || tool === "*") return false
  const last = tool.slice(tool.lastIndexOf(".") + 1)
  const verb = READ_VERB.exec(last)
  if (!verb) return false
  // the verb is a word of its own: `list_x`, `get-x`, `getUser`, `query`; never `listen` or `checkout`
  const next = last.charAt(verb[0].length)
  return next === "" || !/[a-z]/.test(next)
}

/** Decision 0138: whether the rules let `server`'s `tool` run without a card (`tool` null = the CLI did not say). */
export const mcpToolCovered = (
  server: string,
  tool: string | null,
  rules: ReadonlyArray<AllowRule>,
): boolean =>
  rules.some(
    (r) =>
      r.kind === "mcpTool" &&
      r.server === server &&
      (r.tool === "*" ? r.writes === true || mcpToolReads(tool) : tool !== null && r.tool === tool),
  )

const trimSlash = (p: string) => (p.length > 1 ? p.replace(/\/+$/, "") : p)
const hostCovers = (have: string, want: string): boolean => {
  const h = have.toLowerCase()
  const w = want.toLowerCase()
  if (h === w) return true
  if (!h.startsWith("*.")) return false
  const bare = w.startsWith("*.") ? w.slice(2) : w
  return bare.endsWith(h.slice(1)) && bare.length > h.length - 1
}

/**
 * Decision 0138 (kody's "a grant records what it covers"): whether `have` already allows everything `want` would. A
 * folder covers the folders inside it (for the same OS, or any OS when `have` names none), a command prefix the longer
 * prefixes, a `*.` host its subdomains, an MCP rule what `mcpToolCovered` says.
 */
export const ruleCovers = (have: AllowRule, want: AllowRule): boolean => {
  switch (want.kind) {
    case "writableRoot": {
      if (have.kind !== "writableRoot") return false
      if (have.os !== undefined && have.os !== want.os) return false
      const h = trimSlash(have.path)
      const w = trimSlash(want.path)
      return w === h || w.startsWith(h.endsWith("/") ? h : `${h}/`)
    }
    case "command":
      return (
        have.kind === "command" &&
        have.prefix.length <= want.prefix.length &&
        have.prefix.every((word, i) => want.prefix[i] === word)
      )
    case "networkHost":
      return have.kind === "networkHost" && hostCovers(have.host, want.host)
    case "mcpTool":
      if (have.kind !== "mcpTool" || have.server !== want.server) return false
      if (want.tool === "*") return have.tool === "*" && (have.writes === true || want.writes !== true)
      return mcpToolCovered(want.server, want.tool, [have])
  }
}

/** Whether `rules` cover every one of `wanted` (nothing wanted = nothing to cover = false). */
export const rulesCover = (rules: ReadonlyArray<AllowRule>, wanted: ReadonlyArray<AllowRule>): boolean =>
  wanted.length > 0 && wanted.every((w) => rules.some((h) => ruleCovers(h, w)))

/** The rules of several lists in order, each key once (the first wins). */
export const mergeAllowRules = (...lists: ReadonlyArray<ReadonlyArray<AllowRule>>): AllowRule[] => {
  const seen = new Set<string>()
  const out: AllowRule[] = []
  for (const list of lists)
    for (const r of list) {
      const k = allowKey(r)
      if (seen.has(k)) continue
      seen.add(k)
      out.push(r)
    }
  return out
}

/** `entries` plus `add` (new keys only), stamped as added by `by` at `at` from `source`. */
export const withAllowEntries = (
  entries: ReadonlyArray<AllowEntry>,
  add: ReadonlyArray<AllowRule>,
  by: UserId,
  at: number,
  source: AllowEntry["source"],
): AllowEntry[] => {
  const seen = new Set(entries.map(allowEntryKey))
  const out = [...entries]
  for (const rule of add) {
    const k = allowEntryKey({ rule })
    if (seen.has(k)) continue
    seen.add(k)
    out.push({ rule, addedBy: by, addedAt: at, source })
  }
  return out
}

/** Problems with a list (duplicates, too many), in plain words; empty = fine. */
export const allowListProblems = (
  entries: ReadonlyArray<{ readonly rule: AllowRule; readonly repo?: string | undefined }>,
): string[] => {
  const problems: string[] = []
  if (entries.length > ALLOW_MAX) problems.push(`at most ${ALLOW_MAX} entries`)
  const keys = entries.map(allowEntryKey)
  if (new Set(keys).size !== keys.length) problems.push("each entry once")
  return problems
}

/**
 * Decision 0138: what a profile in Auto may do without asking on every run, beyond the job's lists: the package
 * managers' build caches and stores, and the package registries. The runner (feature `owner_grants`) adds them to a run
 * whose effective access mode is Auto; Ask and Full ignore them. Settings shows them as the Auto defaults.
 */
export const AUTO_DEFAULT_ALLOW: ReadonlyArray<AllowRule> = [
  // build caches and package stores
  { kind: "writableRoot", path: "~/Library/pnpm/store", os: "darwin" },
  { kind: "writableRoot", path: "~/Library/Caches/pnpm", os: "darwin" },
  { kind: "writableRoot", path: "~/.local/share/pnpm/store", os: "linux" },
  { kind: "writableRoot", path: "~/.cache/pnpm", os: "linux" },
  { kind: "writableRoot", path: "~/.npm" },
  { kind: "writableRoot", path: "~/Library/Caches/Yarn", os: "darwin" },
  { kind: "writableRoot", path: "~/.cache/yarn", os: "linux" },
  { kind: "writableRoot", path: "~/.bun/install/cache" },
  { kind: "writableRoot", path: "~/.cargo/registry" },
  { kind: "writableRoot", path: "~/.cargo/git" },
  { kind: "writableRoot", path: "~/go/pkg/mod" },
  { kind: "writableRoot", path: "~/Library/Caches/go-build", os: "darwin" },
  { kind: "writableRoot", path: "~/.cache/go-build", os: "linux" },
  { kind: "writableRoot", path: "~/Library/Caches/pip", os: "darwin" },
  { kind: "writableRoot", path: "~/.cache/pip", os: "linux" },
  { kind: "writableRoot", path: "~/Library/Caches/uv", os: "darwin" },
  { kind: "writableRoot", path: "~/.cache/uv", os: "linux" },
  { kind: "writableRoot", path: "~/.gradle/caches" },
  { kind: "writableRoot", path: "~/.m2/repository" },
  { kind: "writableRoot", path: "~/Library/Caches/ms-playwright", os: "darwin" },
  { kind: "writableRoot", path: "~/.cache/ms-playwright", os: "linux" },
  { kind: "writableRoot", path: "~/Library/Caches/turbo", os: "darwin" },
  { kind: "writableRoot", path: "~/.cache/turbo", os: "linux" },
  // package registries
  { kind: "networkHost", host: "registry.npmjs.org" },
  { kind: "networkHost", host: "registry.yarnpkg.com" },
  { kind: "networkHost", host: "pypi.org" },
  { kind: "networkHost", host: "files.pythonhosted.org" },
  { kind: "networkHost", host: "crates.io" },
  { kind: "networkHost", host: "index.crates.io" },
  { kind: "networkHost", host: "static.crates.io" },
  { kind: "networkHost", host: "proxy.golang.org" },
  { kind: "networkHost", host: "sum.golang.org" },
  { kind: "networkHost", host: "repo.maven.apache.org" },
  { kind: "networkHost", host: "rubygems.org" },
]

/**
 * One-click suggestions in Settings, per tool and OS (design §1). Nothing here is on by default; Auto profiles get
 * `AUTO_DEFAULT_ALLOW` from their runner (decision 0138).
 */
export const ALLOW_SUGGESTIONS: ReadonlyArray<{
  readonly label: string
  readonly rules: ReadonlyArray<AllowRule>
}> = [
  {
    label: "wrangler (Cloudflare)",
    rules: [
      { kind: "writableRoot", path: "~/Library/Preferences/.wrangler", os: "darwin" },
      { kind: "writableRoot", path: "~/.config/.wrangler", os: "linux" },
    ],
  },
  {
    label: "pnpm store and cache",
    rules: [
      { kind: "writableRoot", path: "~/Library/pnpm/store", os: "darwin" },
      { kind: "writableRoot", path: "~/Library/Caches/pnpm", os: "darwin" },
      { kind: "writableRoot", path: "~/.local/share/pnpm/store", os: "linux" },
      { kind: "writableRoot", path: "~/.cache/pnpm", os: "linux" },
    ],
  },
  { label: "ps", rules: [{ kind: "command", prefix: ["ps"] }] },
  { label: "kill", rules: [{ kind: "command", prefix: ["kill"] }] },
]

/** The workspace list as the API reads and writes it (`/api/workspaces/:ws/allowlist`). */
export const WorkspaceAllowList = Schema.Struct({ entries: Schema.Array(AllowEntry) })
export type WorkspaceAllowList = typeof WorkspaceAllowList.Type
/** PUT body: the whole list; entries already stored keep who added them, new ones are stamped with the caller. */
export const PutWorkspaceAllowBody = Schema.Struct({
  rules: Schema.Array(
    Schema.Struct({
      rule: AllowRule,
      note: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(200))),
      /** Decision 0138: only runs in this repo. */
      repo: Schema.optionalKey(RepoId),
    }),
  ).check(Schema.isMaxLength(ALLOW_MAX)),
})
export type PutWorkspaceAllowBody = typeof PutWorkspaceAllowBody.Type

/**
 * Decision 0138 (0126 §3): a runner owner's grants: what runs on their machine may do without a card, whoever's work
 * it is. Only the runner's owner reads and changes it (`/api/workspaces/:ws/runners/:runnerId/allowlist`); "Always
 * allow" on a card from their machine, decided by them, adds to it. The hub merges it into every job the runner claims
 * (workspace ∪ project ∪ owner, each resolved for the run's repo).
 */
export const RunnerAllowList = Schema.Struct({ runnerId: RunnerId, entries: Schema.Array(AllowEntry) })
export type RunnerAllowList = typeof RunnerAllowList.Type
/** PUT body of a runner owner's list: as the workspace list's. */
export const PutRunnerAllowBody = PutWorkspaceAllowBody
export type PutRunnerAllowBody = PutWorkspaceAllowBody
// #endregion allow
